import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import type { SupportedSymbol } from '@/lib/symbols';
import type { UserWatchlist, WatchlistCollection } from '@/lib/watchlists';
import { WatchlistEditorSidebar } from './WatchlistEditorSidebar';
import { WatchlistSymbolEditor } from './WatchlistSymbolEditor';
import { watchlistIconClass } from './watchlistEditor.utils';

interface WatchlistEditorProps {
  isOpen: boolean;
  activeWatchlistId: string;
  activeTicker: string;
  collection: WatchlistCollection;
  catalog: readonly SupportedSymbol[];
  isMutating: boolean;
  error: string | null;
  onClose: () => void;
  onCreate: (name: string, tickers: readonly string[]) => Promise<UserWatchlist>;
  onRename: (watchlistId: string, name: string) => Promise<unknown>;
  onDelete: (watchlistId: string) => Promise<unknown>;
  onReorder: (watchlistIds: readonly string[]) => Promise<unknown>;
  onReplaceSymbols: (watchlistId: string, tickers: readonly string[]) => Promise<unknown>;
}

export function WatchlistEditor({
  isOpen,
  activeWatchlistId,
  activeTicker,
  collection,
  catalog,
  isMutating,
  error,
  onClose,
  onCreate,
  onRename,
  onDelete,
  onReorder,
  onReplaceSymbols,
}: WatchlistEditorProps) {
  const [selectedId, setSelectedId] = useState<string | null>(() =>
    getInitialSelection(activeWatchlistId, collection.watchlists),
  );
  const [portalRoot] = useState(() => {
    const root = document.createElement('div');
    root.dataset.watchlistEditorPortal = '';
    return root;
  });
  const wasOpenRef = useRef(false);
  const selected = collection.watchlists.find((watchlist) => watchlist.id === selectedId);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setSelectedId(getInitialSelection(activeWatchlistId, collection.watchlists));
    }
    wasOpenRef.current = isOpen;
  }, [activeWatchlistId, collection.watchlists, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    document.body.appendChild(portalRoot);
    return () => portalRoot.remove();
  }, [isOpen, portalRoot]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-[110] flex items-start justify-center overflow-y-auto p-3 pt-[6vh] min-[640px]:p-6 min-[640px]:pt-[8vh]">
      <button
        type="button"
        aria-label="Dismiss watchlist editor"
        onClick={onClose}
        className="fixed inset-0 cursor-default bg-black/75 backdrop-blur-[2px]"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="watchlist-editor-title"
        className="relative w-full max-w-4xl overflow-hidden rounded-md border border-zinc-700 bg-zinc-950 shadow-2xl shadow-black/70"
      >
        <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-3">
          <div>
            <h2 id="watchlist-editor-title" className="font-mono text-xs font-semibold uppercase">
              Watchlists
            </h2>
            <p className="font-mono text-[10px] text-zinc-500">
              {collection.watchlists.length}/{collection.limits.maxWatchlists} saved lists
            </p>
          </div>
          <button
            type="button"
            aria-label="Close watchlists"
            onClick={onClose}
            className={watchlistIconClass}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid min-h-[420px] min-[720px]:grid-cols-[240px_1fr]">
          <WatchlistEditorSidebar
            watchlists={collection.watchlists}
            selectedId={selectedId}
            maxWatchlists={collection.limits.maxWatchlists}
            maxNameLength={collection.limits.maxNameLength}
            isMutating={isMutating}
            onSelectedIdChange={setSelectedId}
            onCreate={(name) => onCreate(name, [activeTicker])}
            onReorder={onReorder}
          />
          {selected == null ? (
            <section className="flex items-center justify-center p-6 text-center font-mono text-[11px] text-zinc-500">
              Create a watchlist to start adding tickers.
            </section>
          ) : (
            <WatchlistSymbolEditor
              key={selected.id}
              watchlist={selected}
              catalog={catalog}
              maxNameLength={collection.limits.maxNameLength}
              isMutating={isMutating}
              onRename={(name) => onRename(selected.id, name)}
              onDelete={() =>
                onDelete(selected.id).then(() =>
                  setSelectedId(
                    collection.watchlists.find((watchlist) => watchlist.id !== selected.id)?.id ??
                      null,
                  ),
                )
              }
              onReplaceSymbols={(tickers) => onReplaceSymbols(selected.id, tickers)}
            />
          )}
        </div>

        {(error != null || isMutating) && (
          <footer className="border-t border-zinc-800 px-4 py-2 font-mono text-[10px] text-zinc-400">
            {error ?? 'Saving...'}
          </footer>
        )}
      </div>
    </div>,
    portalRoot,
  );
}

function getInitialSelection(
  activeWatchlistId: string,
  watchlists: readonly UserWatchlist[],
): string | null {
  return watchlists.some((watchlist) => watchlist.id === activeWatchlistId)
    ? activeWatchlistId
    : (watchlists[0]?.id ?? null);
}
